import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import Cookies from 'js-cookie';
import { isNil } from 'lodash';

export interface CookieConverter<T> {
    readonly get: (cookie?: string) => T | undefined;
    readonly put: (t: T) => string;
}

export const STRING_COOKIE: CookieConverter<string> = {
    get: (cookie) => cookie,
    put: (value) => value
};

export const BOOLEAN_COOKIE: CookieConverter<boolean> = {
    get: (cookie) => cookie === 'true',
    put: (value) => (value ? 'true' : 'false')
};

type UseCookieResult<T> = [T, Dispatch<SetStateAction<T>>];

type HasDefaults = string | boolean;

function getConverter<T>(converter: CookieConverter<T> | undefined, defaultValue: T): CookieConverter<T> {
    if (isNil(converter)) {
        // funky type stuff.
        if (typeof defaultValue === 'string') {
            return STRING_COOKIE as any;
        } else {
            return BOOLEAN_COOKIE as any;
        }
    } else {
        return converter;
    }
}

// provide overloads for boolean so you don't have to pass a converter
export function useCookie<T extends boolean>(name: string, defaultValue: boolean): UseCookieResult<boolean>;
export function useCookie<T extends boolean>(
    name: string,
    defaultValue: boolean,
    converter: CookieConverter<boolean>
): UseCookieResult<boolean>;

// provide overloads for string so you don't have to pass a converter
export function useCookie<T extends string>(name: string, defaultValue: string): UseCookieResult<string>;
export function useCookie<T extends string>(
    name: string,
    defaultValue: string,
    converter: CookieConverter<string>
): UseCookieResult<string>;

export function useCookie<T>(
    name: string,
    defaultValue: T,
    converter?: T extends HasDefaults ? never : CookieConverter<T>
): UseCookieResult<T> {
    const _converter = getConverter(converter, defaultValue);
    const [value, setValue] = useState<T>(_converter.get(Cookies.get(name)) ?? defaultValue);

    useEffect(() => {
        if (isNil(value)) {
            Cookies.remove(name);
        } else {
            Cookies.set(name, _converter.put(value));
        }
    }, [value]);

    return [value, setValue];
}
